// ============================================================================
// PRELIT-ACTN-001 — Log Agent Action
// ============================================================================
// Medium modal. ActionType + Disposition + Notes + NextActionDate +
// NextActionType. Backdate window read from config.BACKDATING_POLICY.
// info-2.txt §3.1, §4, §5.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import { SyutSelect } from 'components/ui';
import notificationService from 'services/notificationService';
import { useLogAgentAction, useCaptureCommitment } from 'hooks/api/usePreLitigation';
import type { ActionType, Disposition } from 'api/types/preLitigation.types';

const ACTION_TYPE_OPTIONS: { value: ActionType; label: string }[] = [
  { value: 'CALL',         label: 'Phone Call' },
  { value: 'SMS',          label: 'SMS' },
  { value: 'EMAIL',        label: 'Email' },
  { value: 'FIELD_VISIT',  label: 'Field Visit' },
  { value: 'OFFICE_VISIT', label: 'Office Visit' },
];

const DISPOSITION_OPTIONS: { value: Disposition; label: string }[] = [
  { value: 'CONTACTED',            label: 'Contacted' },
  { value: 'NO_REPLY',             label: 'No Reply' },
  { value: 'REFUSED_TO_PAY',       label: 'Refused to Pay' },
  { value: 'COMMITMENT_GIVEN',     label: 'Commitment Given' },
  { value: 'PARTIAL_PAYMENT',      label: 'Partial Payment Received' },
  { value: 'ABUSIVE',              label: 'Abusive / Threatened' },
  { value: 'WRONG_NUMBER',         label: 'Wrong Number' },
  { value: 'NUMBER_NOT_REACHABLE', label: 'Number Not Reachable' },
  { value: 'ADDRESS_INVALID',      label: 'Address Invalid' },
];

interface FormState {
  actionType: ActionType;
  disposition: Disposition;
  notes: string;
  actedAt: string;        // ISO local datetime, backdate-validated
  nextActionDate: string; // YYYY-MM-DD or empty
  nextActionType: ActionType;
  nextActionTypeManual: boolean; // tracks whether the user has manually picked
  // Captured ONLY when disposition === COMMITMENT_GIVEN. Routes the save
  // through capture-commitment (single atomic backend call) so the agent
  // doesn't get a second pop-up after logging the action.
  committedAmount: string;       // string for input control
  committedDate: string;         // YYYY-MM-DD, future, <= window
}

interface LogAgentActionModalProps {
  isOpen: boolean;
  caseId: string;
  caseCode: string;
  /** Current state code — drives "if BROKEN + CONTACTED, will fire retry_contact" hint. */
  currentStateCode?: string | null;
  /** Resolved from config.BACKDATING_POLICY.prelit_action_max_backdate_days for the case's product. */
  maxBackdateDays?: number;
  /** Resolved from config.LITIGATION_POLICY.prelit_commitment_window_days for the case's product. */
  commitmentWindowDays?: number;
  /** Workflow-recommended next action — preselects the **Action Type** field
   *  on first open. Previously this seeded the Next Action Type field; the
   *  agent prefers the inverse: "the channel the workflow plans next IS the
   *  channel I'll use now." */
  defaultActionType?: ActionType | string | null;
  onClose: () => void;
  onLogged: () => void;
}

const ACTION_TYPE_VALUES: ActionType[] = ['CALL', 'SMS', 'EMAIL', 'FIELD_VISIT', 'OFFICE_VISIT'];
const coerceActionType = (v: string | null | undefined): ActionType | null => {
  if (!v) return null;
  const up = v.toUpperCase() as ActionType;
  return (ACTION_TYPE_VALUES as string[]).includes(up) ? up : null;
};

const buildInitial = (defaultActionType?: ActionType | string | null): FormState => {
  const seed = coerceActionType(defaultActionType ?? null) ?? 'CALL';
  return {
    actionType: seed,           // ← workflow-recommended channel becomes the default Action Type
    disposition: 'CONTACTED',
    notes: '',
    actedAt: nowLocalISO(),
    nextActionDate: '',
    nextActionType: seed,        // mirrors actionType (sticky-default logic keeps them aligned
                                 //   until the user manually picks Next Action Type)
    nextActionTypeManual: false,
    committedAmount: '',
    committedDate: '',
  };
};

function nowLocalISO(): string {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16); // YYYY-MM-DDTHH:mm
}

const LogAgentActionModal: React.FC<LogAgentActionModalProps> = ({
  isOpen, caseId, caseCode, currentStateCode, maxBackdateDays = 7, commitmentWindowDays = 90,
  defaultActionType, onClose, onLogged,
}) => {
  const [form, setForm] = useState<FormState>(() => buildInitial(defaultActionType));
  const snapRef = useRef('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);

  const log = useLogAgentAction();
  const capture = useCaptureCommitment();
  const isSaving = log.loading || capture.loading;

  // Capture the workflow-recommended Action Type in a ref so the open-effect
  // reads the latest value without re-firing when the prop changes mid-modal.
  const defaultActionRef = useRef(defaultActionType);
  defaultActionRef.current = defaultActionType;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    const next = buildInitial(defaultActionRef.current);
    setForm(next);
    snapRef.current = JSON.stringify(next);
    setFieldErrors({});
  }, [isOpen]);

  const isDirty = useMemo(() => snapRef.current && JSON.stringify(form) !== snapRef.current, [form]);

  const handleClose = useCallback(() => {
    if (isSaving) return;
    if (isDirty) setDiscardConfirmOpen(true); else onClose();
  }, [isDirty, log.loading, onClose]);

  const setField = <K extends keyof FormState>(field: K, value: FormState[K]) => {
    setFieldErrors(p => { const n = { ...p }; delete n[field as string]; return n; });
    setForm(p => ({ ...p, [field]: value }));
  };

  // Sticky default: when user changes Action Type, also change Next Action Type
  // unless user has manually overridden it (info-2.txt §5).
  const handleActionTypeChange = useCallback((newType: ActionType) => {
    setFieldErrors(p => { const n = { ...p }; delete n.actionType; return n; });
    setForm(prev => ({
      ...prev,
      actionType: newType,
      nextActionType: prev.nextActionTypeManual ? prev.nextActionType : newType,
    }));
  }, []);

  const handleNextActionTypeChange = useCallback((newType: ActionType) => {
    setFieldErrors(p => { const n = { ...p }; delete n.nextActionType; return n; });
    setForm(prev => ({ ...prev, nextActionType: newType, nextActionTypeManual: true }));
  }, []);

  const willFireRetryContact = currentStateCode === 'BROKEN' && form.disposition === 'CONTACTED';
  const isCommitment = form.disposition === 'COMMITMENT_GIVEN';

  const handleSave = useCallback(() => {
    const errors: Record<string, string> = {};
    if (!form.actionType) errors.actionType = 'Required';
    if (!form.disposition) errors.disposition = 'Required';
    if (form.notes.length > 2000) errors.notes = 'Max 2000 chars';

    // Backdate guard
    if (form.actedAt) {
      const acted = new Date(form.actedAt);
      const now = new Date();
      const ms = now.getTime() - acted.getTime();
      const days = ms / (1000 * 60 * 60 * 24);
      if (days > maxBackdateDays) {
        errors.actedAt = `Cannot back-date older than ${maxBackdateDays} day(s).`;
      } else if (acted.getTime() > now.getTime() + 60_000) {
        errors.actedAt = 'Acted At cannot be in the future.';
      }
    }
    if (form.nextActionDate) {
      const next = new Date(form.nextActionDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (next.getTime() < today.getTime()) {
        errors.nextActionDate = 'Next Action Date must be today or later.';
      }
    }

    // Inline commitment validation — only when disposition is COMMITMENT_GIVEN.
    if (isCommitment) {
      const amount = Number(form.committedAmount);
      if (!form.committedAmount || !isFinite(amount) || amount <= 0) {
        errors.committedAmount = 'Commitment amount must be greater than 0.';
      }
      if (!form.committedDate) {
        errors.committedDate = 'Commitment date is required.';
      } else {
        const due = new Date(form.committedDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (due.getTime() < today.getTime()) {
          errors.committedDate = 'Commitment date must be today or in the future.';
        } else if (commitmentWindowDays > 0
                   && due.getTime() > today.getTime() + commitmentWindowDays * 86_400_000) {
          errors.committedDate = `Commitment date cannot be more than ${commitmentWindowDays} day(s) from today.`;
        }
      }
    }

    if (Object.keys(errors).length) { setFieldErrors(errors); return; }
    setFieldErrors({});
    setSaveConfirmOpen(true);
  }, [form, maxBackdateDays, commitmentWindowDays, isCommitment]);

  const handleConfirmSave = useCallback(async () => {
    setSaveConfirmOpen(false);
    try {
      if (isCommitment) {
        // Route to capture-commitment — single backend transaction creates the
        // COMMITMENT_GIVEN action AND the commitment row + fires the workflow
        // transition. No follow-up pop-up needed.
        await capture.run(caseId, {
          actionType: form.actionType,
          notes: form.notes || undefined,
          committedAmount: Number(form.committedAmount),
          committedDate: form.committedDate,
          nextActionDate: form.nextActionDate || undefined,
          nextActionType: form.nextActionType,
          actedAt: form.actedAt ? new Date(form.actedAt).toISOString() : undefined,
        });
        notificationService.toast.success(`Commitment captured on ${caseCode}.`);
      } else {
        await log.run(caseId, {
          actionType: form.actionType,
          disposition: form.disposition,
          notes: form.notes || undefined,
          actedAt: form.actedAt ? new Date(form.actedAt).toISOString() : undefined,
          nextActionDate: form.nextActionDate || undefined,
          nextActionType: form.nextActionType,
        });
        notificationService.toast.success(`${form.actionType} logged on ${caseCode}.`);
      }
      onLogged();
      onClose();
    } catch (err: any) {
      const msg = err?.response?.data?.message
                ?? (isCommitment ? 'Failed to capture commitment.' : 'Failed to log action.');
      notificationService.toast.error(msg, 6000);
    }
  }, [log, capture, caseId, caseCode, form, isCommitment, onLogged, onClose]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-md">
        {isSaving && <LoadingOverlay text={isCommitment ? 'Capturing commitment…' : 'Logging action…'} />}

        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Log Agent Action — {caseCode}</h2>
          <button className="modal-close" onClick={handleClose} disabled={isSaving}>
            <IconSystem.x size={20} />
          </button>
        </div>

        <div className="modal-content">
          <div className="form-grid-2">
            <div className="form-group" data-field="actionType">
              <SyutSelect
                label={<>Action Type <span className="text-danger">*</span></>}
                options={ACTION_TYPE_OPTIONS}
                value={form.actionType}
                onChange={(v) => handleActionTypeChange(v as ActionType)}
                isClearable={false}
                error={fieldErrors.actionType}
              />
            </div>
            <div className="form-group" data-field="disposition">
              <SyutSelect
                label={<>Disposition <span className="text-danger">*</span></>}
                options={DISPOSITION_OPTIONS}
                value={form.disposition}
                onChange={(v) => setField('disposition', v as Disposition)}
                isClearable={false}
                error={fieldErrors.disposition}
              />
            </div>
          </div>

          {willFireRetryContact && (
            <CalloutHint color="#2563eb" bg="#dbeafe" text="Workflow is in BROKEN state — this CONTACTED disposition will auto-retry contacting." />
          )}

          {/* Inline Commitment Details — only when disposition is COMMITMENT_GIVEN.
              Replaces the old "you will be prompted after saving" callout. */}
          {isCommitment && (
            <div style={{
              border: '1px solid #16a34a', borderRadius: 8,
              background: '#f0fdf4',
              padding: '0.75rem 0.85rem',
              display: 'flex', flexDirection: 'column', gap: '0.55rem',
            }}>
              <div style={{
                fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.06em',
                textTransform: 'uppercase', color: '#166534',
                display: 'flex', alignItems: 'center', gap: '0.35rem',
              }}>
                <IconSystem.checkCircle size={13} />
                Commitment Details
                <span style={{ fontWeight: 500, color: '#15803d', textTransform: 'none', letterSpacing: 0 }}>
                  · captured atomically with this action
                </span>
              </div>
              <div className="form-grid-2">
                <div className="form-group" data-field="committedAmount">
                  <label className="form-label">Commitment Amount (₹) <span className="text-danger">*</span></label>
                  <input
                    type="number"
                    inputMode="decimal"
                    min={0}
                    step="0.01"
                    className={`form-input${fieldErrors.committedAmount ? ' form-field-error' : ''}`}
                    value={form.committedAmount}
                    onChange={(e) => setField('committedAmount', e.target.value)}
                    placeholder="e.g. 25000"
                  />
                  {fieldErrors.committedAmount && <div className="form-error-message">{fieldErrors.committedAmount}</div>}
                </div>
                <div className="form-group" data-field="committedDate">
                  <label className="form-label">Commitment Date <span className="text-danger">*</span></label>
                  <input
                    type="date"
                    className={`form-input${fieldErrors.committedDate ? ' form-field-error' : ''}`}
                    value={form.committedDate}
                    onChange={(e) => setField('committedDate', e.target.value)}
                  />
                  <div style={{ fontSize: '0.7rem', color: '#15803d', marginTop: '0.25rem' }}>
                    Today or up to {commitmentWindowDays} day(s) ahead.
                  </div>
                  {fieldErrors.committedDate && <div className="form-error-message">{fieldErrors.committedDate}</div>}
                </div>
              </div>
            </div>
          )}

          {/* Acted At — moved above Notes for a more natural data-entry order
              (When? → What happened? → What's next?). */}
          <div className="form-group" data-field="actedAt">
            <label className="form-label">Acted At</label>
            <input
              type="datetime-local"
              className={`form-input${fieldErrors.actedAt ? ' form-field-error' : ''}`}
              value={form.actedAt}
              onChange={(e) => setField('actedAt', e.target.value)}
            />
            <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
              Back-date up to {maxBackdateDays} day(s) for offline-logged actions.
            </div>
            {fieldErrors.actedAt && <div className="form-error-message">{fieldErrors.actedAt}</div>}
          </div>

          <div className="form-group" data-field="notes">
            <label className="form-label">Notes <span style={{ color: 'var(--text-muted)' }}>(optional, max 2000)</span></label>
            <textarea
              className={`form-textarea${fieldErrors.notes ? ' form-field-error' : ''}`}
              rows={form.actionType === 'FIELD_VISIT' || form.disposition === 'ABUSIVE' ? 6 : 3}
              maxLength={2000}
              value={form.notes}
              onChange={(e) => setField('notes', e.target.value)}
            />
            {fieldErrors.notes && <div className="form-error-message">{fieldErrors.notes}</div>}
          </div>

          <div className="form-grid-2">
            <div className="form-group" data-field="nextActionType">
              <SyutSelect
                label="Next Action Type"
                options={ACTION_TYPE_OPTIONS}
                value={form.nextActionType}
                onChange={(v) => handleNextActionTypeChange(v as ActionType)}
                isClearable={false}
                error={fieldErrors.nextActionType}
              />
            </div>
            <div className="form-group" data-field="nextActionDate">
              <label className="form-label">Next Action Date</label>
              <input
                type="date"
                className={`form-input${fieldErrors.nextActionDate ? ' form-field-error' : ''}`}
                value={form.nextActionDate}
                onChange={(e) => setField('nextActionDate', e.target.value)}
              />
              {fieldErrors.nextActionDate && <div className="form-error-message">{fieldErrors.nextActionDate}</div>}
            </div>
          </div>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={isSaving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={isSaving}>
            {isCommitment ? 'Log Action & Capture Commitment' : 'Log Action'}
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={saveConfirmOpen}
        title="Confirm Action Log"
        message={`Log this ${form.actionType} (${form.disposition}) on case ${caseCode}?`}
        confirmLabel="Log"
        cancelLabel="Cancel"
        variant="default"
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />
      <ConfirmDialog
        open={discardConfirmOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Discard?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => { setDiscardConfirmOpen(false); onClose(); }}
        onCancel={() => setDiscardConfirmOpen(false)}
      />
    </>
  );
};

const CalloutHint: React.FC<{ color: string; bg: string; text: string }> = ({ color, bg, text }) => (
  <div style={{
    padding: '0.5rem 0.75rem', background: bg, color, fontSize: '0.8rem',
    borderRadius: '6px', marginBottom: '0.75rem', border: `1px solid ${color}`,
  }}>{text}</div>
);

const LoadingOverlay: React.FC<{ text: string }> = ({ text }) => (
  <div style={{
    position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)',
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    zIndex: 10, borderRadius: 'inherit', gap: '0.75rem',
  }}>
    <div style={{
      width: '2rem', height: '2rem', border: '3px solid var(--border-color)',
      borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite',
    }} />
    <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>{text}</span>
  </div>
);

export default LogAgentActionModal;
